import React from 'react';

import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import {
  AvatarSize,
  AvatarTypes,
  RelationStatus,
} from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { IUser } from '../../../../types/interfaces/user';
import { getUserViewName } from '../../../../utils/common/shared-util';
import AppUserAvatar from '../../../designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import {
  AcceptSubscriptionRequestButton,
  CancelSubscriptionRequestButton,
  DropFromSubscribersButton,
  RejectSubscriptionRequestButton,
  RequestSubscriptionButton,
  SubscribeButton,
  UnsubscribeButton,
  subscriptionIconOnlyButtonSx,
} from '../../../designe/buttons/subscription-buttons';
import { useStyles } from './UserBigItem.styles';
import { useUserBigItem } from './useUserBigItem';

interface UserBigItemProps {
  user: IUser;
  communityId?: string; // one day to do it better. Now I am tired and lazy
}

export default function UserBigItem(props: UserBigItemProps) {
  const { user, communityId } = props;
  const { navigate } = useNavigationStack();
  const { data: currentUser } = useGetMyDetails();
  const isCurrentUser = user.id === currentUser.id;
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const isListIconOnly = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    onSubscribe,
    onUnsubscribe,
    onSubscriptionStatusChange,
    isSubscribeLoading,
    isUnsubscribeLoading,
    isApproveStatusLoading,
    isRejectStatusLoading,
    isDropStatusLoading,
  } = useUserBigItem(user, communityId);

  function clickOnUserHandler() {
    navigate(Paths.users.toUser(user.id));
  }

  return (
    <div className={classes.userWrapper} onClick={clickOnUserHandler}>
      <div className={classes.avatarWrapper}>
        <AppUserAvatar
          entity={user}
          size={AvatarSize.m}
          type={AvatarTypes.round}
        />
      </div>
      <div className={classes.userDetails}>
        <div className={classes.infoWrapper}>
          <Typography variant="h3">{getUserViewName(user)}</Typography>
        </div>
        <div className={classes.controlWrapper}>
          {!isCurrentUser && (
            <>
              {user.permissions?.canSubscribe && (
                <SubscribeButton
                  iconOnly={isListIconOnly}
                  loading={isSubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscribe(user.id);
                  }}
                />
              )}
              {user.permissions?.canRequestSubscription && (
                <RequestSubscriptionButton
                  iconOnly={isListIconOnly}
                  loading={isSubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscribe(user.id);
                  }}
                />
              )}
              {user.permissions?.canUnsubscribe && (
                <UnsubscribeButton
                  iconOnly={isListIconOnly}
                  loading={isUnsubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnsubscribe(user.id);
                  }}
                />
              )}
              {user.permissions?.canCancelRequestSubscription && (
                <CancelSubscriptionRequestButton
                  iconOnly={isListIconOnly}
                  loading={isUnsubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnsubscribe(user.id);
                  }}
                />
              )}
              {user.permissions?.canApproveRequest && (
                <AcceptSubscriptionRequestButton
                  iconOnly={isListIconOnly}
                  loading={isApproveStatusLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscriptionStatusChange(
                      user.id,
                      RelationStatus.approved,
                      'approve',
                    );
                  }}
                />
              )}
              {user.permissions?.canRejectRequest && (
                <RejectSubscriptionRequestButton
                  iconOnly={isListIconOnly}
                  loading={isRejectStatusLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscriptionStatusChange(
                      user.id,
                      RelationStatus.rejected,
                      'reject',
                    );
                  }}
                />
              )}
              {user.permissions?.canDropFromSubscribers && (
                <DropFromSubscribersButton
                  iconOnly={isListIconOnly}
                  loading={isDropStatusLoading}
                  sx={isListIconOnly ? subscriptionIconOnlyButtonSx : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscriptionStatusChange(
                      user.id,
                      RelationStatus.rejected,
                      'drop',
                    );
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
